/**
 * Movement System — grounded walking + airborne steering + momentum drain.
 *
 * Extracted from aiming.ts. Single responsibility: resolve player movement
 * intent during the aiming phase (and post-fire air control).
 *
 * LEFT/RIGHT (or A/D): Move tank horizontally (momentum-gated, slope-scaled)
 */

import { getSurfaceNormal, isPointInTerrain } from '../terrain/grid';
import { PHYSICS, MOVEMENT, MOMENTUM, TRACTION, TUMBLE } from '../constants';
import { calculateMoveCost } from '../physics/movement';
import { clampToWorldX } from '../physics/tanks';
import { applyBurstAirControl } from './burst-di';
import type { GameState, Player } from '../types';
import type { InputProvider } from '../input';
import { Keys } from 'bonkjs';
import { mirrorWalking } from '../physics/transitions';
import { lookupTank } from '../tanks';

/** Result from movement update — visual effect triggers */
export interface MovementResult {
  reversed: boolean;    // direction reversal detected (visual FX)
}

/**
 * Ramped recovery air control — reusable for any tumble_recovery context.
 * Applies directional vx nudge based on recovery ramp progress.
 * Returns true if input was applied.
 */
export function applyRecoveryAirControl(player: Player, input: InputProvider): boolean {
  if (!player.tumble || player.actionState.tag !== 'tumble_recovery' || player.grounded) return false;
  const ramp = player.tumbleRecoveryMax > 0
    ? player.tumbleRecoveryFrame / player.tumbleRecoveryMax
    : 1;
  const control = TUMBLE.RECOVERY_CONTROL_MIN + (1 - TUMBLE.RECOVERY_CONTROL_MIN) * ramp;
  const accel = TUMBLE.RECOVERY_AIR_ACCEL * control;
  let moveDir = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) moveDir -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) moveDir += 1;
  if (moveDir !== 0) {
    player.vx += moveDir * accel;
    return true;
  }
  return false;
}

/** Grounded walking + airborne steering + momentum drain. Called once per aiming frame. */
export function updateMovement(state: GameState, input: InputProvider): MovementResult {
  const player = state.players[state.currentPlayer];

  let reversed = false;

  // --- Horizontal movement (LEFT/RIGHT or A/D) ---
  let moveDir = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) moveDir -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) moveDir += 1;

  // Grounded state — use physics-authoritative flag (set by terrain contact in tanks.ts).
  // Previously used getGroundY proximity check which could false-positive when airborne
  // over terrain peaks (causing walk-state flicker after burst launches).
  const grounded = player.grounded;

  // Post-burst velocity air control — free, no momentum cost, bypasses momentum gate
  if (moveDir !== 0 && !player.tumble && !grounded && !player.liftActive && player.burstFlying) {
    applyBurstAirControl(player, moveDir);
  }

  // === MOVEMENT (grounded + airborne) ===
  // During tumble, ragdoll physics handles movement — no walking or steering.
  if (moveDir !== 0 && !player.tumble && (player.momentum > 0 || !grounded)) {
    if (grounded) {
      // Grounded movement — costs momentum, slope-affected via surface normal
      const destX = clampToWorldX(player.x + moveDir * MOVEMENT.DASH_SPEED);

      // Get surface normal at destination center height. Sampling at feet (player.y + ECB_RADIUS)
      // can be INSIDE terrain bumps at the destination, producing steep/horizontal normals.
      // Center height (25px above ground) means only tall terrain features (real walls) affect
      // the pre-check; small bumps are invisible → default flat normal → tanks.ts handles physics.
      const { nx, ny } = getSurfaceNormal(destX, player.y);
      const absNy = Math.abs(ny);

      // Wall pre-check — prevent false momentum deduction (lenient: let tanks.ts handle actual wall rejection)
      if (absNy < TRACTION.WALK_WALL_NY) {
        // Wall at destination — skip movement entirely
      } else {
        // Derive slope cost from normal: goingUphill = moving against the X component
        const goingUphill = moveDir * nx < 0;
        const slopeAngle = 1 - absNy; // 0 = flat, ~0.65 = wall threshold
        const rise = goingUphill
          ? MOVEMENT.MAX_CLIMBABLE_RISE * slopeAngle / (1 - TRACTION.WALK_WALL_NY)
          : 0; // downhill → flat cost

        const { cost: rawCost, speedMult } = calculateMoveCost(rise);
        const effectiveSpeedMult = rise > MOVEMENT.MAX_CLIMBABLE_RISE
          ? Math.max(MOVEMENT.MIN_STEEP_SPEED_MULT, speedMult)
          : speedMult;
        const moveCostMult = lookupTank(player.tankId).movementCostMultiplier ?? 1.0;
        const cost = (player.prevMoveDir === 0 ? rawCost * MOMENTUM.STARTUP_COST_MULT : rawCost) * moveCostMult;

        if (player.momentum >= cost) {
          const maxDist = MOVEMENT.DASH_SPEED * effectiveSpeedMult;
          const newX = clampToWorldX(player.x + moveDir * maxDist);

          // X intent for tanks.ts — it resolves both X and Y together
          player.walkDeltaX = newX - player.x;
          player.walkCatchupTimer = TRACTION.CATCHUP_FRAMES;
          if (player.actionState.tag !== 'walking') mirrorWalking(player);
          player.rotation += moveDir * MOVEMENT.TILT_RATE;
          player.momentum -= cost;
        }
      }
    } else if (!player.liftActive) {
      // Airborne WITHOUT lift — free steering (no momentum cost).
      // When lift is active, updateHopLift handles all airborne thrust.
      const prevAirX = player.x;
      const rawDestX = player.x + moveDir * MOVEMENT.DASH_SPEED;
      player.x = clampToWorldX(rawDestX);

      // Terrain guard — don't push into ceiling tiles or any solid terrain
      if (isPointInTerrain(player.x, player.y) ||
        isPointInTerrain(player.x + PHYSICS.ECB_RADIUS * moveDir, player.y)) {
        player.x = prevAirX;
      } else {
        // Visual spin while airborne strafing
        player.rotation += moveDir * MOVEMENT.TILT_RATE;
      }
    }
  }

  // Detect direction reversal (grounded, was moving, now moving opposite)
  if (moveDir !== 0 && player.prevMoveDir !== 0 && moveDir !== player.prevMoveDir && grounded) {
    reversed = true;
  }

  // === MOMENTUM DRAIN ===
  // Activation: first movement input starts the drain clock
  if (moveDir !== 0 && !player.momentumActivated) {
    player.momentumActivated = true;
  }

  if (player.momentumActivated && grounded) {
    // No time-based drain — momentum only spent on movement actions

    // Idle tracking (kept for defense phase)
    if (moveDir !== 0) {
      player.idleFrames = 0;
    } else {
      player.idleFrames++;
    }

    // Clamp to 0
    if (player.momentum < 0) player.momentum = 0;
  }

  // Update direction tracking
  player.prevMoveDir = player.moveDir;
  player.moveDir = moveDir;

  return { reversed };
}

/**
 * Post-fire airborne steering — lets the active player air-control
 * during awaiting_next_turn (e.g. falling into a crater they just made).
 * No momentum cost, no aim/charge — just horizontal nudge.
 */
export function updatePostFireMovement(state: GameState, input: InputProvider): void {
  const player = state.players[state.currentPlayer];
  if (!player.alive) return;

  // Tumble gate — during hitstun, no control at all.
  // During recovery, apply ramped air control instead of normal.
  if (player.tumble) {
    applyRecoveryAirControl(player, input);
    return;
  }

  if (player.grounded) return; // grounded — no air control

  // Post-burst velocity air control — real trajectory change alongside position teleport
  if (player.burstFlying) {
    let postFireMoveDir = 0;
    if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) postFireMoveDir -= 1;
    if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) postFireMoveDir += 1;
    if (postFireMoveDir !== 0) {
      applyBurstAirControl(player, postFireMoveDir);
    }
  }

  let moveDir = 0;
  if (input.getKey(Keys.ArrowLeft) || input.getKey(Keys.A)) moveDir -= 1;
  if (input.getKey(Keys.ArrowRight) || input.getKey(Keys.D)) moveDir += 1;
  if (moveDir === 0) return;

  const prevX = player.x;
  const rawDestX = player.x + moveDir * MOVEMENT.DASH_SPEED;
  player.x = clampToWorldX(rawDestX);

  // Terrain guard — don't push into ceiling tiles or any solid terrain
  if (isPointInTerrain(player.x, player.y) ||
    isPointInTerrain(player.x + PHYSICS.ECB_RADIUS * moveDir, player.y)) {
    player.x = prevX;
  } else {
    // Visual spin while airborne strafing (post-fire)
    player.rotation += moveDir * MOVEMENT.TILT_RATE;
  }
}
